/**
 * usePrayers Hook
 *
 * Manages prayer requests using React Query with filters, pagination, and mutations.
 *
 * Benefits of React Query:
 * - Automatic deduplication (same groupId + filters = same cached response)
 * - Smart background refetching
 * - Built-in cache management (no manual cache needed)
 * - Prevents duplicate API calls during re-renders
 * - Infinite query support for cursor-based pagination
 * - Optimistic updates for mutations
 */

import { useState, useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';
import type {
  Prayer,
  SubmitPrayerData,
  PrayerStatus,
  PrayerFilters,
  PrivacyLevel,
} from '@flock-types/prayer.types';
import { isPrayerLifted } from '@flock-types/prayer.types';

/**
 * Hook options for filtering and pagination
 */
export interface UsePrayersOptions {
  groupId: string | null;
  status?: 'active' | 'answered' | 'all';
  type?: 'prayer' | 'praise' | 'all';
  limit?: number;
  myPrayersOnly?: boolean;
}

/**
 * Hook return value with prayers data and mutation functions
 */
export interface UsePrayersReturn {
  // Data
  prayers: Prayer[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  filters: PrayerFilters;

  // Pagination
  loadMore: () => Promise<void>;

  // Mutations (optimistic updates)
  setFilters: (filters: PrayerFilters) => void;
  submitPrayer: (data: SubmitPrayerData) => Promise<void>;
  markAsPrayed: (prayerId: string, note?: string) => Promise<void>;
  addEncouragement: (prayerId: string, note: string) => Promise<void>;
  markAsAnswered: (prayerId: string, answerText?: string) => Promise<void>;
  deletePrayer: (prayerId: string) => Promise<void>;
  editPrayer: (prayerId: string, prayerText: string) => Promise<void>;
  liftPrayer: (prayerId: string) => Promise<void>;
  isLifting: boolean;
  unliftPrayer: (prayerId: string) => Promise<void>;
  isUnlifting: boolean;

  // Sync status
  isSyncing: boolean;
  pendingCount: number;

  // Refresh
  refetch: () => Promise<void>;
}

/**
 * Raw prayer data from backend API (snake_case fields)
 */
interface PrayerApiData {
  id?: string;
  firestore_doc_id?: string;
  organization_id?: string;
  organizationId?: string;
  group_id?: string;
  groupId?: string;
  user_id?: string;
  userId?: string;
  user_name?: string;
  userName?: string;
  prayer_text?: string;
  prayerText?: string;
  type?: 'prayer' | 'praise';
  privacy_level?: string;
  privacyLevel?: string;
  status?: string;
  prayed_for_by?: string[];
  prayedForBy?: string[];
  prayed_for_count?: number;
  prayedForCount?: number;
  encouragement_notes?: Array<{
    id: string;
    userId: string;
    userName: string;
    note: string;
    createdAt: string;
  }>;
  encouragementNotes?: Array<{
    id: string;
    userId: string;
    userName: string;
    note: string;
    createdAt: string;
  }>;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  answered_at?: string;
  answeredAt?: string;
  answer_text?: string;
  answerText?: string;
  lifted_at?: string;
  liftedAt?: string;
  lifted_count?: number;
  liftedCount?: number;
  encouragement_reactions?: Record<string, Record<string, string[]>>;
  encouragementReactions?: Record<string, Record<string, string[]>>;
}

/**
 * Backend API response for prayers
 */
interface PrayersApiResponse {
  success: boolean;
  data: {
    prayers: PrayerApiData[];
    groupName: string;
    hasMore: boolean;
    nextCursor: string | null;
    viewerRole: 'leader' | 'member';
  };
}

interface PrayersPage {
  prayers: Prayer[];
  hasMore: boolean;
  nextCursor: string | null;
}

const DEFAULT_LIMIT = 50;

/**
 * Transform API response to Prayer type.
 * Exported for reuse by useCombinedPrayers (cross-group views).
 */
export function transformPrayer(p: PrayerApiData, organizationId: string, groupId: string): Prayer {
  return {
    id: p.id || p.firestore_doc_id || '',
    organizationId: p.organization_id || p.organizationId || organizationId,
    groupId: p.group_id || p.groupId || groupId,
    userId: p.user_id || p.userId || '',
    userName: p.user_name || p.userName || 'Anonymous',
    prayerText: p.prayer_text || p.prayerText || '',
    type: p.type || 'prayer',
    privacyLevel: (p.privacy_level || p.privacyLevel || 'public') as PrivacyLevel,
    status: (p.status || 'active') as PrayerStatus,
    prayedForBy: p.prayed_for_by || p.prayedForBy || [],
    prayedForCount: p.prayed_for_count || p.prayedForCount || 0,
    encouragementNotes: p.encouragement_notes || p.encouragementNotes || [],
    createdAt: p.created_at || p.createdAt || new Date().toISOString(),
    updatedAt: p.updated_at || p.updatedAt || new Date().toISOString(),
    answeredAt: p.answered_at || p.answeredAt,
    answerText: p.answer_text || p.answerText,
    liftedAt: p.lifted_at || p.liftedAt,
    liftedCount: p.lifted_count || p.liftedCount || 0,
    encouragementReactions: p.encouragement_reactions || p.encouragementReactions || {},
  };
}

/**
 * Sort prayers for the wall: currently-lifted first (most recently lifted on
 * top), then everything else newest-first. Mirrors the mobile prayer feed.
 */
export function sortPrayersLiftedFirst<T extends Prayer>(prayers: T[]): T[] {
  const millis = (v: Prayer['createdAt'] | undefined): number => {
    if (!v) return 0;
    if (typeof v === 'string') return new Date(v).getTime();
    if (typeof (v as any).toDate === 'function') return (v as any).toDate().getTime();
    return new Date(v as any).getTime();
  };
  return [...prayers].sort((a, b) => {
    const aLifted = isPrayerLifted(a);
    const bLifted = isPrayerLifted(b);
    if (aLifted !== bLifted) return aLifted ? -1 : 1;
    if (aLifted && bLifted) return millis(b.liftedAt) - millis(a.liftedAt);
    return millis(b.createdAt) - millis(a.createdAt);
  });
}

/**
 * Fetch prayers page from API
 */
async function fetchPrayersPage(
  groupId: string,
  organizationId: string,
  filters: PrayerFilters,
  cursor?: string
): Promise<PrayersPage> {
  // Build query params
  const params = new URLSearchParams();
  params.set('limit', DEFAULT_LIMIT.toString());

  // Send status filter to backend
  if (filters.status && filters.status !== 'all') {
    params.set('status', filters.status);
  }
  if (cursor) {
    params.set('startAfter', cursor);
  }

  const response = await api.get<PrayersApiResponse>(
    `/prayers/groups/${groupId}/prayers?${params.toString()}`
  );

  // Handle both { data: { prayers } } and direct { prayers } response shapes
  const prayerData: PrayerApiData[] = response?.data?.prayers || (response as { prayers?: PrayerApiData[] })?.prayers || [];

  const prayers = prayerData.map((p) => transformPrayer(p, organizationId, groupId));

  return {
    prayers,
    hasMore: response.data?.hasMore ?? prayerData.length === DEFAULT_LIMIT,
    nextCursor: response.data?.nextCursor ?? (prayerData.length > 0 ? prayerData[prayerData.length - 1].id ?? null : null),
  };
}

/**
 * Hook for prayers using backend API with React Query
 *
 * Features:
 * - Fetches prayers from backend API (with RLS security)
 * - Filtering by status and type
 * - Infinite pagination support
 * - Optimistic updates for mutations
 */
export function usePrayers(groupId: string | null): UsePrayersReturn {
  const { currentOrganization, user, isSwitchingOrg } = useAuth();
  const queryClient = useQueryClient();

  // Filters state (kept local since it affects query key)
  const [filters, setFilters] = useState<PrayerFilters>({
    status: 'active',
    type: 'all',
  });

  // Query key for this group and filters
  const queryKey = ['prayers', groupId, filters.status];

  // Main infinite query for prayers
  const prayersQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchPrayersPage(groupId!, currentOrganization!.id, filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    // Gate on !isSwitchingOrg so refetches during the 1.5–7.5s claims-
    // verification window don't fire with NEW_TOKEN + OLD_GROUP_ID and
    // 403 against the new org's RLS context.
    enabled: !!currentOrganization?.id && !!groupId && !isSwitchingOrg,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Flatten pages, apply client-side type filter, lifted prayers float first
  const prayers = useMemo(() => {
    const allPrayers = prayersQuery.data?.pages.flatMap((page) => page.prayers) ?? [];

    // Apply type filter (client-side since API may not support it)
    const filtered =
      filters.type && filters.type !== 'all'
        ? allPrayers.filter((p) => p.type === filters.type)
        : allPrayers;

    return sortPrayersLiftedFirst(filtered);
  }, [prayersQuery.data, filters.type]);

  // Helper to get all prayers from cache (for optimistic updates)
  const getAllPrayersFromCache = (): Prayer[] => {
    return prayersQuery.data?.pages.flatMap((page) => page.prayers) ?? [];
  };

  // Mutation: Submit prayer
  const submitMutation = useMutation({
    mutationFn: async (data: SubmitPrayerData) => {
      await api.post('/prayers', data);
      return data;
    },
    onMutate: async (data) => {
      if (!user || !currentOrganization) return;

      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      // Optimistic update - prepend new prayer
      const optimisticPrayer: Prayer = {
        id: `temp-${Date.now()}`,
        organizationId: currentOrganization.id,
        groupId: data.groupId,
        userId: user.id,
        userName: user.name,
        prayerText: data.prayerText,
        type: data.type,
        privacyLevel: data.privacyLevel,
        status: 'active' as PrayerStatus,
        prayedForBy: [],
        prayedForCount: 0,
        encouragementNotes: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages?.length) return old;
        return {
          ...old,
          pages: old.pages.map((page: PrayersPage, index: number) => {
            if (index === 0) {
              return { ...page, prayers: [optimisticPrayer, ...page.prayers] };
            }
            return page;
          }),
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
    },
  });

  // Mutation: Mark as prayed
  const markAsPrayedMutation = useMutation({
    // NOTE (fix vs mobile): the dedup check must NOT live inside mutationFn.
    // React Query runs onMutate (the optimistic cache write) BEFORE
    // mutationFn, so reading the cache here always finds the user already in
    // prayedForBy and skips the API call — "I Prayed" never persists. The
    // dedup guard moved into the markAsPrayed wrapper below, which checks the
    // cache before mutate() fires.
    mutationFn: async ({ prayerId, note }: { prayerId: string; note?: string }) => {
      await api.put(`/prayers/${prayerId}/mark-prayed`, { note });
      return { prayerId, note };
    },
    onMutate: async ({ prayerId, note }) => {
      if (!user) return;

      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: PrayersPage) => ({
            ...page,
            prayers: page.prayers.map((prayer: Prayer) => {
              if (prayer.id === prayerId) {
                const alreadyPrayed = prayer.prayedForBy.includes(user.id);
                const updatedPrayer: Prayer = {
                  ...prayer,
                  prayedForBy: alreadyPrayed ? prayer.prayedForBy : [...prayer.prayedForBy, user.id],
                  prayedForCount: alreadyPrayed ? prayer.prayedForCount : prayer.prayedForCount + 1,
                  updatedAt: new Date().toISOString(),
                };

                // Add encouragement note if provided
                if (note && note.trim()) {
                  updatedPrayer.encouragementNotes = [
                    ...prayer.encouragementNotes,
                    {
                      id: `temp-${Date.now()}`,
                      userId: user.id,
                      userName: user.name,
                      note: note.trim(),
                      createdAt: new Date().toISOString(),
                    },
                  ];
                }

                return updatedPrayer;
              }
              return prayer;
            }),
          })),
        };
      });

      return { previousData };
    },
    onError: (err: any, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      // Check for 403 Forbidden - user is likely pending approval
      if (err?.response?.status === 403) {
        throw new Error('This feature will be available once your membership is approved.');
      }
      throw err;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
    },
  });

  // Mutation: Add encouragement
  const addEncouragementMutation = useMutation({
    mutationFn: async ({ prayerId, note }: { prayerId: string; note: string }) => {
      await api.post(`/prayers/${prayerId}/encouragement`, { note });
      return { prayerId, note };
    },
    onMutate: async ({ prayerId, note }) => {
      if (!user) return;

      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: PrayersPage) => ({
            ...page,
            prayers: page.prayers.map((prayer: Prayer) => {
              if (prayer.id === prayerId) {
                return {
                  ...prayer,
                  encouragementNotes: [
                    ...prayer.encouragementNotes,
                    {
                      id: `temp-${Date.now()}`,
                      userId: user.id,
                      userName: user.name,
                      note,
                      createdAt: new Date().toISOString(),
                    },
                  ],
                  updatedAt: new Date().toISOString(),
                };
              }
              return prayer;
            }),
          })),
        };
      });

      return { previousData };
    },
    onError: (err: any, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      // Check for 403 Forbidden - user is likely pending approval
      if (err?.response?.status === 403) {
        throw new Error('This feature will be available once your membership is approved.');
      }
      throw err;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
    },
  });

  // Mutation: Edit prayer
  const editMutation = useMutation({
    mutationFn: async ({ prayerId, prayerText }: { prayerId: string; prayerText: string }) => {
      await api.put(`/prayers/${prayerId}/text`, { prayerText });
      return { prayerId, prayerText };
    },
    onMutate: async ({ prayerId, prayerText }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: PrayersPage) => ({
            ...page,
            prayers: page.prayers.map((prayer: Prayer) => {
              if (prayer.id === prayerId) {
                return {
                  ...prayer,
                  prayerText,
                  updatedAt: new Date().toISOString(),
                };
              }
              return prayer;
            }),
          })),
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
    },
  });

  // Mutation: Mark as answered
  const markAsAnsweredMutation = useMutation({
    mutationFn: async ({ prayerId, answerText }: { prayerId: string; answerText?: string }) => {
      await api.put(`/prayers/${prayerId}/mark-answered`, { answerText });
      return { prayerId, answerText };
    },
    onMutate: async ({ prayerId, answerText }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: PrayersPage) => ({
            ...page,
            prayers: page.prayers.map((prayer: Prayer) => {
              if (prayer.id === prayerId) {
                return {
                  ...prayer,
                  status: 'answered' as PrayerStatus,
                  answerText,
                  answeredAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                };
              }
              return prayer;
            }),
          })),
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
    },
  });

  // Mutation: Lift prayer (float to top for 48h)
  const liftPrayerMutation = useMutation({
    mutationFn: async ({ prayerId }: { prayerId: string }) => {
      await api.put(`/prayers/${prayerId}/lift`);
      return { prayerId };
    },
    onMutate: async ({ prayerId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            prayers: page.prayers.map((p: any) =>
              p.id === prayerId
                ? {
                    ...p,
                    liftedAt: new Date().toISOString(),
                    liftedCount: (p.liftedCount || 0) + 1,
                  }
                : p
            ),
          })),
        };
      });

      return { previousData };
    },
    onError: (err: any, variables: any, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });

  // Mutation: Unlift prayer (cancel lifted state early)
  const unliftPrayerMutation = useMutation({
    mutationFn: async ({ prayerId }: { prayerId: string }) => {
      await api.put(`/prayers/${prayerId}/unlift`);
      return { prayerId };
    },
    onMutate: async ({ prayerId }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            prayers: page.prayers.map((p: any) =>
              p.id === prayerId
                ? { ...p, liftedAt: null }
                : p
            ),
          })),
        };
      });

      return { previousData };
    },
    onError: (err: any, variables: any, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });

  // Mutation: Delete prayer
  const deleteMutation = useMutation({
    mutationFn: async (prayerId: string) => {
      await api.delete(`/prayers/${prayerId}`);
      return prayerId;
    },
    onMutate: async (prayerId) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: PrayersPage) => ({
            ...page,
            prayers: page.prayers.filter((prayer: Prayer) => prayer.id !== prayerId),
          })),
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
    },
  });

  // Wrapper functions to match original interface
  const submitPrayer = useCallback(async (data: SubmitPrayerData): Promise<void> => {
    if (!user || !currentOrganization) {
      throw new Error('User not authenticated');
    }
    await submitMutation.mutateAsync(data);
  }, [submitMutation, user, currentOrganization]);

  const markAsPrayed = useCallback(async (prayerId: string, note?: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    // Dedup BEFORE mutate(): once mutate runs, the optimistic update makes
    // the cache indistinguishable from "already prayed" (see mutationFn note).
    if (!note) {
      const currentData = queryClient.getQueryData(queryKey) as any;
      const existingPrayer = currentData?.pages
        ?.flatMap((p: any) => p.prayers)
        ?.find((p: any) => p.id === prayerId);
      if (existingPrayer?.prayedForBy?.includes(user.id)) {
        return;
      }
    }
    await markAsPrayedMutation.mutateAsync({ prayerId, note });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAsPrayedMutation, user, queryClient, JSON.stringify(queryKey)]);

  const addEncouragement = useCallback(async (prayerId: string, note: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    await addEncouragementMutation.mutateAsync({ prayerId, note });
  }, [addEncouragementMutation, user]);

  const editPrayer = useCallback(async (prayerId: string, prayerText: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    await editMutation.mutateAsync({ prayerId, prayerText });
  }, [editMutation, user]);

  const markAsAnswered = useCallback(async (prayerId: string, answerText?: string): Promise<void> => {
    await markAsAnsweredMutation.mutateAsync({ prayerId, answerText });
  }, [markAsAnsweredMutation]);

  const deletePrayer = useCallback(async (prayerId: string): Promise<void> => {
    await deleteMutation.mutateAsync(prayerId);
  }, [deleteMutation]);

  const liftPrayer = useCallback(async (prayerId: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    await liftPrayerMutation.mutateAsync({ prayerId });
  }, [liftPrayerMutation, user]);

  const unliftPrayer = useCallback(async (prayerId: string): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    await unliftPrayerMutation.mutateAsync({ prayerId });
  }, [unliftPrayerMutation, user]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (prayersQuery.hasNextPage && !prayersQuery.isFetchingNextPage) {
      await prayersQuery.fetchNextPage();
    }
  }, [prayersQuery]);

  const refetch = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['prayers', groupId] });
  }, [queryClient, groupId]);

  // Calculate pending count from all active mutations
  const pendingCount =
    (submitMutation.isPending ? 1 : 0) +
    (markAsPrayedMutation.isPending ? 1 : 0) +
    (addEncouragementMutation.isPending ? 1 : 0) +
    (editMutation.isPending ? 1 : 0) +
    (markAsAnsweredMutation.isPending ? 1 : 0) +
    (deleteMutation.isPending ? 1 : 0) +
    (liftPrayerMutation.isPending ? 1 : 0) +
    (unliftPrayerMutation.isPending ? 1 : 0);

  const isSyncing =
    prayersQuery.isRefetching ||
    submitMutation.isPending ||
    markAsPrayedMutation.isPending ||
    addEncouragementMutation.isPending ||
    editMutation.isPending ||
    markAsAnsweredMutation.isPending ||
    deleteMutation.isPending ||
    liftPrayerMutation.isPending ||
    unliftPrayerMutation.isPending;

  return {
    prayers,
    loading: prayersQuery.isLoading,
    error: prayersQuery.error?.message ?? null,
    hasMore: prayersQuery.hasNextPage ?? false,
    filters,
    setFilters,
    loadMore,
    submitPrayer,
    markAsPrayed,
    addEncouragement,
    markAsAnswered,
    deletePrayer,
    editPrayer,
    liftPrayer,
    isLifting: liftPrayerMutation.isPending,
    unliftPrayer,
    isUnlifting: unliftPrayerMutation.isPending,
    isSyncing,
    pendingCount,
    refetch,
  };
}
